/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';

import {
  type HtmlAttributes,
  type PolymorphicCommonProps,
} from '../_common/types';

type ProgressBarProps = {
  /** The heading displayed above the progress bar */
  heading?: string;
  /** The current progress value, should be between 0 and 100 */
  value: number;
  /** Size of the progress bar */
  size: 'small' | 'large';
  /** Additional class names */
  className?: string;
  /** Additional HTML attributes */
  htmlAttributes?: HtmlAttributes<'div'>;
};

export const ProgressBar = <T extends React.ElementType = 'div'>({
  as,
  heading,
  value,
  size = 'small',
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, ProgressBarProps>) => {
  const classes = classNames('ndl-progress-bar-wrapper', className, {
    'ndl-large': size === 'large',
    'ndl-small': size === 'small',
  });
  const Component: React.ElementType = as ?? 'div';

  return (
    <Component
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {heading && (
        <div className="ndl-header">
          <span className="ndl-heading">{heading}</span>
          <span className="ndl-progress-number">{`${value}%`}</span>
        </div>
      )}
      <div className="ndl-progress-bar-wrapper-inner">
        <div
          className="ndl-progress-bar-container"
          role="progressbar"
          title="progress bar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={value}
        >
          <div className="ndl-progress-bar" style={{ width: `${value}%` }} />
        </div>
        <div
          className="ndl-progress-bar-shadow"
          style={{ width: `${value}%` }}
        />
      </div>
    </Component>
  );
};
